import type { ReactNode } from 'react'
import type { ChartData } from 'chart.js'
import type { DashboardPeriodParams } from '@/lib/dashboard-period'
import type { AnalyticsDashboardStats, OperatorOverviewRow, ProjectRow, SlaTimelinePoint } from '@/types/api'

export const currentColor = 'rgba(59, 130, 246, 0.9)'
export const previousColor = 'rgba(148, 163, 184, 0.5)'
export const operatorColors = ['#8B5CF6', '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#14B8A6', '#6366F1', '#84CC16', '#F97316']
export const hourlyFallbackColors = ['#4F86F7', '#D92D20', '#FFF04A', '#8E3B3B', '#6CA7FF', '#7C6B66', '#65B35B', '#56E3C0', '#6D63D8', '#B0B0B0', '#BDB76B', '#4E9BFF']
export const sourceColors = ['#229ED9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#6366F1', '#64748B']
export const PRODUCT_TICKETS_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const
export const ANALYTICS_TOOLTIP_WIDTH = 300
export const ANALYTICS_TOOLTIP_MAX_HEIGHT = 320
export const ANALYTICS_TOOLTIP_PADDING = 10
export const PRODUCT_TOOLTIP_HEIGHT = 340
export type ProductStatsRow = NonNullable<AnalyticsDashboardStats['by_project']>[number]
export type ChannelStatsRow = NonNullable<AnalyticsDashboardStats['by_channel']>[number]
export type ProjectChannelStatsRow = NonNullable<AnalyticsDashboardStats['by_project_channel']>[number]
export type ProductTooltipData = {
  name: string
  color: string
  total: number
  previous: number
  percent: number
  opened: number
  inWork: number
  resolved: number
  closed: number
  emergency: number
  avgFirstResponseMinutes?: number | null
  avgCloseMinutes?: number | null
}
export type ChartRowsTooltipData = {
  title: string
  color?: string
  imageUrl?: string
  emoji?: string
  rows: Array<{ label: ReactNode; value: string; tone?: 'blue' | 'muted' | 'danger' | 'default' }>
}
export const aiReasonLabels = {
  transfer_after_system_hint: 'После системного сообщения',
  transfer_planned_instruction: 'Плановая передача по инструкции',
  transfer_unknown_answer: 'Не знает ответ / эскалация',
  transfer_user_requested_operator: 'Попросили оператора',
  transfer_watchdog_timeout: 'Watchdog: таймаут',
  transfer_watchdog_requeue_limit: 'Watchdog: лимит переочередей',
  transfer_other: 'Прочие передачи',
} as const
export type AiReasonKey = keyof typeof aiReasonLabels
export const aiReasonHints = {
  transfer_after_system_hint: 'Передача после системного сообщения Мозгобота с предложением передать обращение оператору.',
  transfer_planned_instruction: 'Плановая передача по инструкции проекта. Сейчас это отдельная категория для auto_transfer_after_data_collected.',
  transfer_unknown_answer: 'Мозгобот не смог уверенно продолжить сам: не знает ответ, нужна эскалация, клиент отказался или не дал нужные данные, либо прислал вложение после запроса данных.',
  transfer_user_requested_operator: 'Клиент сам запросил перевод на оператора.',
  transfer_watchdog_timeout: 'Передача из-за watchdog: клиент долго не отвечает и срок ожидания истек.',
  transfer_watchdog_requeue_limit: 'Передача из-за watchdog: превышен лимит повторных постановок обращения в очередь.',
  transfer_other: 'Все остальные передачи, которые не попали в выделенные категории выше.',
} as const
export const analyticsTooltipStyles = {
  tooltip: {
    background: '#303030',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: 12,
    boxShadow: '0 16px 36px rgba(0, 0, 0, 0.28)',
    padding: '10px 12px',
    fontFamily: 'var(--font-sans)',
    width: ANALYTICS_TOOLTIP_WIDTH,
    maxWidth: 'calc(100vw - 32px)',
  },
  arrow: {
    background: '#303030',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
}
export const productTooltipStyles = {
  tooltip: {
    background: 'transparent',
    color: '#fff',
    border: 0,
    borderRadius: 0,
    boxShadow: 'none',
    padding: 0,
    fontFamily: 'var(--font-sans)',
    maxWidth: 'calc(100vw - 32px)',
  },
  arrow: {
    background: '#303030',
  },
}

export const chartTooltipBase = {
  backgroundColor: '#303030',
  titleColor: '#ffffff',
  bodyColor: '#ffffff',
  borderColor: 'rgba(255, 255, 255, 0.08)',
  borderWidth: 1,
  cornerRadius: 12,
  caretSize: 6,
  padding: ANALYTICS_TOOLTIP_PADDING,
  boxPadding: 4,
  titleFont: { size: 12, weight: 700 },
  bodyFont: { size: 12, weight: 700 },
  footerFont: { size: 11, weight: 600 },
  bodySpacing: 5,
  footerSpacing: 5,
  displayColors: false,
} as const

export const PROJECT_RESPONSE_TIME_COLOR = 'rgba(59, 130, 246, 0.88)'
export const PROJECT_RESPONSE_TIME_SOLID = '#3B82F6'
export const PROJECT_RESOLUTION_TIME_COLOR = 'rgba(239, 68, 68, 0.82)'
export const PROJECT_RESOLUTION_TIME_SOLID = '#EF4444'

export function pad2(value: number) {
  return String(value).padStart(2, '0')
}

export function toLocalDatetimeValue(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '—'
  const safeValue = Number(value)
  return Number.isFinite(safeValue) ? safeValue.toLocaleString('ru-RU') : '—'
}

export function formatMinutes(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  const totalSeconds = Math.max(0, Math.round(Number(value) * 60))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const parts: string[] = []
  if (hours) parts.push(`${hours} ч`)
  if (minutes) parts.push(`${minutes} мин`)
  if (seconds || parts.length === 0) parts.push(`${seconds} сек`)
  return parts.join(' ')
}

export function formatDurationClock(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  const totalSeconds = Math.max(0, Math.round(Number(value) * 60))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
}

export function formatTooltipPercent(value: number | null | undefined): string {
  const safeValue = Number(value ?? 0)
  return `${(Number.isFinite(safeValue) ? safeValue : 0).toFixed(1).replace('.', ',')}%`
}

export function formatKtu(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  return Number(value).toFixed(2)
}

export function shortLabel(value: string, max = 12): string {
  const clean = value.trim() || '—'
  return clean.length > max ? `${clean.slice(0, Math.max(1, max - 1))}…` : clean
}

export function wrapTooltipText(value: string | null | undefined, maxLine = 30, maxLines = 3): string[] {
  const text = (value || '—').replace(/\s+/g, ' ').trim()
  if (text.length <= maxLine) return [text]
  const words = text.split(' ')
  const lines: string[] = []
  let line = ''
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word
    if (next.length > maxLine && line) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  })
  if (line) lines.push(line)
  if (lines.length <= maxLines) return lines
  const visible = lines.slice(0, maxLines)
  visible[maxLines - 1] = `${visible[maxLines - 1].replace(/…$/, '')}…`
  return visible
}

export function initials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
  return (value.trim() || '?').slice(0, 2).toUpperCase()
}

export function operatorKey(op: OperatorOverviewRow): string {
  return op.operator_id || op.operator_name || op.operator_email || 'unknown'
}

export function productKey(row: NonNullable<AnalyticsDashboardStats['by_project']>[number]): string {
  return row.project_id || row.project_name || 'unknown'
}

export function channelSourceKey(row: { channel_type?: string | null }): string {
  const key = String(row.channel_type || 'unknown').trim().toLowerCase() || 'unknown'
  return key === 'webchat' ? 'widget' : key
}

export function channelSourceLabel(channelType: string | null | undefined): string {
  const key = String(channelType || '').trim().toLowerCase()
  if (key === 'telegram') return 'Telegram'
  if (key === 'widget' || key === 'webchat') return 'Виджет'
  if (key === 'email') return 'Почта'
  if (key === 'whatsapp') return 'WhatsApp'
  if (key === 'connector') return 'Коннектор'
  if (key === 'facebook') return 'Facebook'
  if (key === 'instagram') return 'Instagram'
  if (key === 'vk') return 'VK'
  if (key === 'custom') return 'Custom'
  return 'Неизвестно'
}

export function normalizeChannelStats(rows: Array<{ channel_type?: string | null; total?: number | null }> | undefined | null): ChannelStatsRow[] {
  const map = new Map<string, number>()
  ;(rows ?? []).forEach((row) => {
    const key = channelSourceKey(row)
    map.set(key, (map.get(key) || 0) + (Number(row.total) || 0))
  })
  return Array.from(map.entries())
    .map(([channel_type, total]) => ({ channel_type, total }))
    .filter((row) => row.total > 0)
    .sort((a, b) => b.total - a.total)
}

export function normalizeProjectChannelStats(rows: Array<{ project_id?: string | null; project_name?: string | null; channel_type?: string | null; total?: number | null }> | undefined | null): ProjectChannelStatsRow[] {
  const map = new Map<string, ProjectChannelStatsRow>()
  ;(rows ?? []).forEach((row) => {
    const projectId = String(row.project_id || '').trim()
    if (!projectId) return
    const channelType = channelSourceKey(row)
    const key = `${projectId}:${channelType}`
    const current = map.get(key)
    map.set(key, {
      project_id: projectId,
      project_name: row.project_name ?? current?.project_name ?? null,
      channel_type: channelType,
      total: (Number(current?.total) || 0) + (Number(row.total) || 0),
    })
  })
  return Array.from(map.values()).filter((row) => row.total > 0)
}

export function buildProjectSourceChartData(rows: ProjectChannelStatsRow[], previousRows: ProjectChannelStatsRow[], projects: ProjectRow[]) {
  const projectMap = new Map<string, { project_id: string; project_name?: string | null; total: number; previous: number; sources: Map<string, number>; previousSources: Map<string, number> }>()
  rows.forEach((row) => {
    const projectId = row.project_id
    const current = projectMap.get(projectId) ?? { project_id: projectId, project_name: row.project_name, total: 0, previous: 0, sources: new Map(), previousSources: new Map() }
    current.project_name = current.project_name || row.project_name
    current.total += Number(row.total) || 0
    current.sources.set(row.channel_type, (current.sources.get(row.channel_type) || 0) + (Number(row.total) || 0))
    projectMap.set(projectId, current)
  })
  previousRows.forEach((row) => {
    const projectId = row.project_id
    const current = projectMap.get(projectId) ?? { project_id: projectId, project_name: row.project_name, total: 0, previous: 0, sources: new Map(), previousSources: new Map() }
    current.project_name = current.project_name || row.project_name
    current.previous += Number(row.total) || 0
    current.previousSources.set(row.channel_type, (current.previousSources.get(row.channel_type) || 0) + (Number(row.total) || 0))
    projectMap.set(projectId, current)
  })
  const projectRows = Array.from(projectMap.values())
    .filter((row) => row.total > 0 || row.previous > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)
  const sourceKeys = Array.from(new Set(projectRows.flatMap((row) => [...Array.from(row.sources.keys()), ...Array.from(row.previousSources.keys())])))
    .sort((a, b) => {
      const totalA = projectRows.reduce((sum, row) => sum + (row.sources.get(a) || 0) + (row.previousSources.get(a) || 0), 0)
      const totalB = projectRows.reduce((sum, row) => sum + (row.sources.get(b) || 0) + (row.previousSources.get(b) || 0), 0)
      return totalB - totalA
    })
  const labels = projectRows.map((row) => {
    const project = projects.find((item) => item.id === row.project_id)
    return shortLabel(project?.name || row.project_name || '—', 12)
  })
  const chartData: ChartData<'bar'> = {
    labels,
    datasets: [
      ...sourceKeys.map((sourceKey, index) => ({
        label: channelSourceLabel(sourceKey),
        data: projectRows.map((row) => row.sources.get(sourceKey) || 0),
        backgroundColor: sourceColors[index % sourceColors.length],
        borderRadius: 8,
        maxBarThickness: 18,
        stack: 'current',
      })),
      ...sourceKeys.map((sourceKey) => ({
        label: `${channelSourceLabel(sourceKey)} — предыдущий период`,
        data: projectRows.map((row) => row.previousSources.get(sourceKey) || 0),
        backgroundColor: previousColor,
        borderRadius: 8,
        maxBarThickness: 18,
        stack: 'previous',
      })),
    ],
  }
  const sourceTotals = sourceKeys.map((sourceKey, index) => ({
    channel_type: sourceKey,
    total: projectRows.reduce((sum, row) => sum + (row.sources.get(sourceKey) || 0), 0),
    previous: projectRows.reduce((sum, row) => sum + (row.previousSources.get(sourceKey) || 0), 0),
    color: sourceColors[index % sourceColors.length],
  }))
  return {
    chartData,
    projectRows,
    sourceTotals,
    total: projectRows.reduce((sum, row) => sum + row.total, 0),
    previousTotal: projectRows.reduce((sum, row) => sum + row.previous, 0),
  }
}

export function pctChange(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null
  return ((current - previous) / Math.abs(previous)) * 100
}

export function changeLine(current: number, previous: number, lowerIsBetter = false): { text: string; tone: 'green' | 'red' | 'gray' } {
  if (previous === 0) {
    if (current === 0) return { text: 'Без изменений к предыдущему периоду', tone: 'gray' }
    return { text: `Было 0, стало ${formatNumber(current)}`, tone: lowerIsBetter ? 'red' : 'green' }
  }
  const change = pctChange(current, previous)
  if (change == null || Math.abs(change) < 0.1) return { text: 'Без изменений к предыдущему периоду', tone: 'gray' }
  const better = lowerIsBetter ? change < 0 : change > 0
  const direction = change > 0 ? 'выше' : 'ниже'
  const quality = better ? 'лучше' : 'хуже'
  return { text: `На ${Math.abs(change).toFixed(0)}% ${direction}, чем в предыдущем периоде (${quality})`, tone: better ? 'green' : 'red' }
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value.toFixed(1)}%`
}

export function compactChangeLine(
  current: number | null | undefined,
  previous: number | null | undefined,
  lowerIsBetter = false,
): { text: string; tone: 'green' | 'red' | 'gray' } | null {
  if (current == null || previous == null) return null
  const cur = Number(current)
  const prev = Number(previous)
  if (!Number.isFinite(cur) || !Number.isFinite(prev)) return null
  if (prev === 0) {
    if (cur === 0) return { text: 'Без изменений', tone: 'gray' }
    return { text: `Было 0`, tone: lowerIsBetter ? 'red' : 'green' }
  }
  const change = ((cur - prev) / Math.abs(prev)) * 100
  if (!Number.isFinite(change) || Math.abs(change) < 0.1) return { text: 'Без изменений', tone: 'gray' }
  const better = lowerIsBetter ? change < 0 : change > 0
  const direction = change > 0 ? 'выше' : 'ниже'
  const arrow = change > 0 ? '▴' : '▾'
  return {
    text: `${arrow} на ${Math.abs(change).toFixed(0)}% ${direction}`,
    tone: better ? 'green' : 'red',
  }
}

export function compactSummaryText(summary: { text: string; tone: 'green' | 'red' | 'gray' }): string {
  const text = summary.text || 'Без изменений'
  const match = text.match(/на\s+([0-9.,]+)%\s+(выше|ниже)/i)
  if (match) {
    const arrow = match[2].toLowerCase() === 'выше' ? '▲' : '▼'
    return `${arrow} на ${match[1]}% ${match[2].toLowerCase()}`
  }
  if (text.toLowerCase().startsWith('без изменений')) return 'Без изменений'
  if (text.toLowerCase().startsWith('было 0')) return text
  return text
}

export function formatHourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

export function formatHourRange(hour: number): string {
  return `${formatHourLabel(hour)}-${formatHourLabel((hour + 1) % 24)}`
}

export function withAlpha(hex: string, alpha: number): string {
  const safe = hex.replace('#', '').trim()
  const normalized = safe.length === 3 ? safe.split('').map((c) => c + c).join('') : safe.padEnd(6, '0').slice(0, 6)
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function buildHourlyAxis(maxValue: number): { step: number; max: number } {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return { step: 1, max: 10 }
  const rough = maxValue / 10
  const power = 10 ** Math.floor(Math.log10(Math.max(rough, 1)))
  const normalized = rough / power
  const nice =
    normalized <= 1 ? 1 :
      normalized <= 2 ? 2 :
        normalized <= 2.5 ? 2.5 :
          normalized <= 5 ? 5 : 10
  const step = nice * power
  return { step, max: step * 10 }
}

export function periodToTicketParams(params: DashboardPeriodParams): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {
    exclude_emergency: false,
    sort_by: 'created_at',
    sort_order: 'desc',
  }
  if (params.hours) out.created_within_hours = params.hours
  else if (params.days) out.created_within_hours = params.days * 24
  else if (params.date_time_from || params.date_time_to) {
    if (params.date_time_from) out.created_date_from = params.date_time_from.slice(0, 10)
    if (params.date_time_to) out.created_date_to = params.date_time_to.slice(0, 10)
  }
  return out
}

export function summaryTone(summary: { tone: 'green' | 'red' | 'gray' }): 'good' | 'bad' | 'neutral' {
  if (summary.tone === 'green') return 'good'
  if (summary.tone === 'red') return 'bad'
  return 'neutral'
}


export function aggregateTiming(rows: OperatorOverviewRow[], metric: 'first_response' | 'assignment' | 'reply' | 'resolution'): number | null {
  let count = 0
  let sum = 0
  for (const row of rows) {
    const rowCount = row[`${metric}_count`]
    const rowSum = row[`${metric}_sum_minutes`]
    if (rowCount == null || rowSum == null || !Number.isFinite(rowCount) || !Number.isFinite(rowSum) || rowCount <= 0) continue
    count += rowCount
    sum += rowSum
  }
  return count > 0 ? sum / count : null
}


export function aggregateSlaTimeline(rows: SlaTimelinePoint[], previous = false): number | null {
  const count = rows.reduce((sum, row) => sum + (previous ? row.previous_tickets ?? 0 : row.tickets ?? 0), 0)
  const met = rows.reduce((sum, row) => sum + (previous ? row.previous_met ?? 0 : row.met ?? 0), 0)
  return count > 0 ? met * 100 / count : null
}
